import React, { useEffect, useState } from 'react';
import { Alert, Button, Divider, Grid, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import apiClient from '../../../shared/api/client';
import { getPacientes } from '../api/pacientesApi';
import { buildDefaultEvaluationRanges } from '../utils/evaluationDateRanges';

export default function ExportDataPanel() {
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const defaultRanges = buildDefaultEvaluationRanges();
  const [preStart, setPreStart] = useState(defaultRanges.preStart);
  const [preEnd, setPreEnd] = useState(defaultRanges.preEnd);
  const [postStart, setPostStart] = useState(defaultRanges.postStart);
  const [postEnd, setPostEnd] = useState(defaultRanges.postEnd);
  const [excelError, setExcelError] = useState('');
  const [downloadingExcel, setDownloadingExcel] = useState(false);

  useEffect(() => {
    getPacientes().then(setPatients).catch((err) => console.error('Error fetching patients', err));
  }, []);

  const downloadBlob = (response, fallbackName) => {
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fallbackName);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Grid item xs={12}>
      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h6" gutterBottom color="#0D6AEE">Exportar Datos de Pacientes</Typography>
        <Button variant="contained" onClick={async () => downloadBlob(await apiClient.get('/pacientes/export', { responseType: 'blob' }), 'datos_pacientes.csv')}>
          Descargar Todo en CSV
        </Button>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom color="#0D6AEE">Exportar Base de Datos en Excel</Typography>
        <Typography variant="body2">Indique los rangos de fecha para las evaluaciones PRE y POST que desea incluir en el Excel.</Typography>
        {excelError && <Alert severity="error">{excelError}</Alert>}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Paper elevation={0} sx={{ p: 2, flex: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Rango PRE</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Fecha inicio" type="date" value={preStart} onChange={(e) => setPreStart(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
              <TextField label="Fecha fin" type="date" value={preEnd} onChange={(e) => setPreEnd(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
            </Stack>
          </Paper>
          <Paper elevation={0} sx={{ p: 2, flex: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Rango POST</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Fecha inicio" type="date" value={postStart} onChange={(e) => setPostStart(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
              <TextField label="Fecha fin" type="date" value={postEnd} onChange={(e) => setPostEnd(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
            </Stack>
          </Paper>
        </Stack>
        <Button variant="contained" color="secondary" disabled={downloadingExcel} onClick={async () => {
          if (!preStart || !preEnd || !postStart || !postEnd) {
            setExcelError('Debe completar los rangos de fechas para PRE y POST.');
            return;
          }
          try {
            setExcelError('');
            setDownloadingExcel(true);
            const response = await apiClient.get('/pacientes/export/xlsx', { params: { preStart, preEnd, postStart, postEnd }, responseType: 'blob' });
            downloadBlob(response, 'pacientes_pre-post.xlsx');
          } catch (error) {
            console.error('Error downloading Excel data', error);
            setExcelError('No se pudo descargar el archivo. Intente nuevamente.');
          } finally {
            setDownloadingExcel(false);
          }
        }}>
          {downloadingExcel ? 'Descargando...' : 'Descargar Excel'}
        </Button>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom color="#0D6AEE">Exportar Datos de un Paciente</Typography>
        <TextField select label="Paciente" value={selectedPatient} onChange={(e) => setSelectedPatient(e.target.value)} fullWidth>
          {patients.map((patient) => <MenuItem key={patient.id} value={patient.id}>{patient.firstname} {patient.lastname}</MenuItem>)}
        </TextField>
        <TextField label="Fecha" type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
        <Button variant="contained" disabled={!selectedPatient || !selectedDate} onClick={async () => {
          try {
            const response = await apiClient.get(`/pacientes/${selectedPatient}/export`, { params: { date: selectedDate }, responseType: 'blob' });
            downloadBlob(response, `paciente_${selectedPatient}_${selectedDate}.csv`);
          } catch (error) {
            console.error('Error downloading patient data', error);
          }
        }}>
          Descargar Paciente en CSV
        </Button>
      </Paper>
    </Grid>
  );
}
